/**
 * Data-aware suggested (starter) questions for the Ask-DAM chat.
 *
 * R-145 — the empty-state prompt chips should only surface questions
 * whose UNDERLYING data actually exists for the active site / portfolio.
 * We don't want to dangle "Why is my LCOE above the NREL ATB reference?"
 * at a site with no LCOE run, or "What does the geological survey say?"
 * when no geotechnical doc has been uploaded — those just produce a
 * "data not in context" answer.
 *
 * The availability map is derived CLIENT-SIDE from the in-store site
 * object (site scope) or the portfolio site list (portfolio scope) — no
 * extra round-trip. Presence is intentionally PERMISSIVE for fields the
 * site object can't cheaply prove (e.g. an LCOE run lives in a
 * subcollection, not on the site doc): we approximate from the closest
 * available signal and prefer to SHOW a borderline question rather than
 * hide a useful one. Categories the site object CAN prove absent (no
 * SED, no docs, no boundary) reliably hide their questions.
 */
import type { SiteEntity } from '../../types/site';

/** Data categories a starter question can depend on. */
export type DataCategory =
  | 'sed'
  | 'sim'
  | 'lcoe'
  | 'report'
  | 'financial'
  | 'documents'
  | 'boundary'
  | 'coverage'
  | 'intent';

/** Availability flags for one scope (a site, or the portfolio). */
export type AvailabilityMap = Record<DataCategory, boolean>;

/**
 * The subset of {@link SiteEntity} the availability map reads. Typed as a
 * `Partial<SiteEntity>` so a real store site object (or a test fixture
 * built from one) passes without casting.
 */
type SiteLike = Partial<SiteEntity>;

function nonEmpty(arr: unknown): boolean {
  return Array.isArray(arr) && arr.length > 0;
}

/** Build an availability map from a single site object. */
export function siteAvailability(site: SiteLike | null | undefined): AvailabilityMap {
  if (!site) {
    // Unknown site → permissive (the server still gates on real data and
    // the model is told to handle missing data gracefully).
    return {
      sed: true, sim: true, lcoe: true, report: true, financial: true,
      documents: true, boundary: true, coverage: true, intent: true,
    };
  }
  const hasSim = nonEmpty(site.simulationIds);
  const hasReport = nonEmpty(site.reportIds) || site.latestReportScore != null || !!site.latestReportId;
  return {
    sed: !!site.sedId || !!site.sedStoragePath || (!!site.sedStatus && site.sedStatus !== 'failed'),
    sim: hasSim,
    // LCOE runs live in a subcollection the site object doesn't enumerate;
    // a sim is the prerequisite for an LCOE run, so gate the LCOE question
    // on having at least one simulation (avoids dangling it on bare sites).
    lcoe: hasSim,
    report: hasReport,
    financial: !!site.financialAssumptions && Object.keys(site.financialAssumptions).length > 0,
    documents: nonEmpty(site.documentIds) || nonEmpty(site.documents),
    boundary: nonEmpty(site.siteBoundaries) || nonEmpty(site.siteLayers) || site.siteArea != null,
    coverage: !!site.coverage && Object.keys(site.coverage).length > 0,
    intent: !!site.projectIntent && Object.keys(site.projectIntent).length > 0,
  };
}

/** Build an availability map across a portfolio (OR of per-site presence). */
export function portfolioAvailability(sites: SiteLike[]): AvailabilityMap {
  const acc: AvailabilityMap = {
    sed: false, sim: false, lcoe: false, report: false, financial: false,
    documents: false, boundary: false, coverage: false, intent: false,
  };
  for (const s of sites) {
    const a = siteAvailability(s);
    (Object.keys(acc) as DataCategory[]).forEach((k) => { acc[k] = acc[k] || a[k]; });
  }
  return acc;
}

interface StarterQuestion {
  text: string;
  /** Categories that must ALL be available for this question to show. */
  needs: DataCategory[];
}

const SITE_QUESTIONS: StarterQuestion[] = [
  { text: 'Summarise everything you know about this site.', needs: [] },
  { text: 'Why is my LCOE above the NREL ATB reference?', needs: ['lcoe'] },
  { text: 'Which permits will trigger on this site?', needs: ['sed'] },
  { text: 'What are the biggest data gaps blocking a confident verdict?', needs: ['sed'] },
  { text: 'Walk me through the latest report verdict and what drove the score.', needs: ['report'] },
  { text: 'What do the uploaded documents add that the SED did not have?', needs: ['documents'] },
  { text: 'Break down the financial-model assumptions behind the economics.', needs: ['financial'] },
  { text: 'Which data providers came back empty, and how do I fill them?', needs: ['coverage'] },
];

const PORTFOLIO_QUESTIONS: StarterQuestion[] = [
  { text: 'Summarise the state of my whole portfolio.', needs: [] },
  { text: 'Rank my sites by LCOE.', needs: ['lcoe'] },
  { text: 'Which sites have a completed feasibility verdict, and what are the scores?', needs: ['report'] },
  { text: 'Which sites are closest to permit blockers?', needs: ['sed'] },
  { text: 'Where are the biggest data gaps across my sites?', needs: ['sed'] },
];

/**
 * Resolve the starter questions to show for the given scope, filtered to
 * those whose underlying data exists. A `null` map (unknown scope) shows
 * all questions. Always returns at least the scope-default catch-all so
 * the empty state is never blank.
 */
export function dataAwareQuestions(
  scope: 'site' | 'portfolio',
  availability: AvailabilityMap | null,
): string[] {
  const pool = scope === 'site' ? SITE_QUESTIONS : PORTFOLIO_QUESTIONS;
  const shown = pool.filter(
    (q) => !availability || q.needs.every((cat) => availability[cat]),
  );
  // Guarantee a non-empty list (the needs:[] catch-all always survives).
  return (shown.length > 0 ? shown : pool.filter((q) => q.needs.length === 0)).map((q) => q.text);
}

/** Convenience: availability for whatever scope the panel is showing. */
export function availabilityForScope(
  resolvedSiteId: string | null,
  sites: SiteEntity[],
): { scope: 'site' | 'portfolio'; map: AvailabilityMap } {
  if (resolvedSiteId) {
    const site = sites.find((s) => s.id === resolvedSiteId) ?? null;
    return { scope: 'site', map: siteAvailability(site) };
  }
  return { scope: 'portfolio', map: portfolioAvailability(sites) };
}
